import { useState, useEffect } from 'react';

const Services = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const services = [
    {
      icon: "📱",
      title: "Mobile Application Development",
      shortTitle: "Mobile Apps",
      description: "Build powerful native and cross-platform mobile applications with cutting-edge technologies for iOS and Android platforms.",
      features: ["iOS & Android Native", "React Native & Flutter", "Progressive Web Apps", "App Store Optimization"],
      category: "development",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    {
      icon: "🌐",
      title: "Web Development Services",
      shortTitle: "Web Development",
      description: "Create modern, responsive websites and web applications that deliver exceptional user experiences across all devices.",
      features: ["React & Next.js", "Responsive Design", "E-commerce Solutions", "CMS Integration"],
      category: "development",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    {
      icon: "🧩",
      title: "Custom Software Solutions",
      shortTitle: "Custom Software",
      description: "Enterprise-grade custom software solutions designed specifically to meet your unique business requirements and workflows.",
      features: ["Enterprise Software", "API Development", "Cloud Architecture", "Microservices"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    },
    {
      icon: "🔐",
      title: "Device Policy Controller (DPC) & EMM",
      shortTitle: "DPC / EMM",
      description: "Advanced device policy controller and enterprise mobility management solutions for complete mobile device security and control.",
      features: ["Device Policy Management", "Mobile Device Security", "Fleet Management", "Remote Administration"],
      category: "security",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
    },
    {
      icon: "☁️",
      title: "Cloud Solutions & DevOps",
      shortTitle: "Cloud & DevOps",
      description: "Scalable cloud infrastructure and DevOps automation services to enhance your deployment pipeline and system reliability.",
      features: ["AWS & Azure & GCP", "CI/CD Pipelines", "Container Orchestration", "Infrastructure as Code"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
    },
    {
      icon: "🎨",
      title: "UI/UX Design Services",
      shortTitle: "UI/UX Design",
      description: "User-centered design services that create intuitive, engaging interfaces optimized for conversion and user satisfaction.",
      features: ["User Research", "Wireframing & Prototyping", "Design Systems", "Usability Testing"],
      category: "design",
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
    },
    {
      icon: "🤖",
      title: "AI & Machine Learning Solutions",
      shortTitle: "AI/ML",
      description: "Harness the power of artificial intelligence and machine learning to automate processes and gain intelligent insights from data.",
      features: ["Predictive Analytics", "Natural Language Processing", "Computer Vision", "Chatbot Development"],
      category: "ai",
      gradient: "linear-gradient(135deg, #8EC5FC 0%, #E0C3FC 100%)"
    },
    {
      icon: "🛡️",
      title: "Cybersecurity Services",
      shortTitle: "Cybersecurity",
      description: "Comprehensive security solutions to protect your digital assets from threats, vulnerabilities, and cyber attacks.",
      features: ["Security Audits", "Penetration Testing", "Threat Detection", "Compliance Management"],
      category: "security",
      gradient: "linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)"
    },
    {
      icon: "📊",
      title: "Data Analytics & BI",
      shortTitle: "Analytics",
      description: "Transform raw data into actionable insights with advanced analytics and business intelligence solutions.",
      features: ["Data Visualization", "Business Intelligence", "Big Data Processing", "Real-time Analytics"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #00d2ff 0%, #3a47d5 100%)"
    },
    {
      icon: "🔗",
      title: "Blockchain Development",
      shortTitle: "Blockchain",
      description: "Build secure, transparent, and decentralized applications using blockchain technology and smart contracts.",
      features: ["Smart Contracts", "DApp Development", "NFT Platforms", "Cryptocurrency Solutions"],
      category: "development",
      gradient: "linear-gradient(135deg, #f8b500 0%, #fceabb 100%)"
    },
    {
      icon: "🎮",
      title: "Game Development",
      shortTitle: "Gaming",
      description: "Create immersive gaming experiences for mobile, web, and desktop platforms with stunning graphics and engaging gameplay.",
      features: ["2D/3D Games", "Unity & Unreal Engine", "Multiplayer Systems", "AR/VR Gaming"],
      category: "development",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    {
      icon: "🛒",
      title: "E-commerce Solutions",
      shortTitle: "E-commerce",
      description: "Complete e-commerce platforms with secure payment integration, inventory management, and seamless shopping experiences.",
      features: ["Custom Storefronts", "Payment Gateway Integration", "Inventory Management", "Multi-vendor Marketplace"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    {
      icon: "📡",
      title: "IoT Solutions",
      shortTitle: "IoT",
      description: "Connect and manage smart devices with Internet of Things solutions for home automation, industrial monitoring, and more.",
      features: ["Device Integration", "Real-time Monitoring", "Data Processing", "Smart Automation"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    },
    {
      icon: "💬",
      title: "Chatbot & Voice Assistant",
      shortTitle: "Conversational AI",
      description: "Intelligent chatbots and voice assistants powered by AI to enhance customer engagement and automate support.",
      features: ["NLP Integration", "Multi-platform Support", "Voice Recognition", "24/7 Automation"],
      category: "ai",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
    },
    {
      icon: "📹",
      title: "Video Streaming & OTT Platforms",
      shortTitle: "Streaming",
      description: "Build scalable video streaming platforms with live broadcasting, on-demand content, and adaptive streaming technology.",
      features: ["Live Streaming", "Video on Demand", "Content Management", "Multi-device Support"],
      category: "development",
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
    },
    {
      icon: "🏥",
      title: "Healthcare Software Solutions",
      shortTitle: "Healthcare",
      description: "HIPAA-compliant healthcare software including telemedicine, patient management, and electronic health records systems.",
      features: ["Telemedicine Apps", "EHR/EMR Systems", "Patient Portal", "Medical Billing"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
    },
    {
      icon: "🎓",
      title: "E-Learning Platforms",
      shortTitle: "EdTech",
      description: "Interactive e-learning platforms with course management, live classes, assessments, and student progress tracking.",
      features: ["LMS Development", "Live Video Classes", "Assessment Tools", "Progress Analytics"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #8EC5FC 0%, #E0C3FC 100%)"
    },
    {
      icon: "🚗",
      title: "Fleet Management Systems",
      shortTitle: "Fleet Management",
      description: "Comprehensive fleet management solutions with GPS tracking, route optimization, and vehicle maintenance scheduling.",
      features: ["GPS Tracking", "Route Optimization", "Fuel Management", "Maintenance Alerts"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)"
    },
    {
      icon: "💳",
      title: "FinTech Solutions",
      shortTitle: "FinTech",
      description: "Secure financial technology solutions including digital wallets, payment processing, and investment platforms.",
      features: ["Digital Wallets", "Payment Processing", "Investment Platforms", "Banking Apps"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #00d2ff 0%, #3a47d5 100%)"
    },
    {
      icon: "📱",
      title: "Progressive Web Apps (PWA)",
      shortTitle: "PWA",
      description: "Fast, reliable, and engaging web applications that work offline and provide native app-like experiences.",
      features: ["Offline Functionality", "Push Notifications", "App-like Experience", "Cross-platform"],
      category: "development",
      gradient: "linear-gradient(135deg, #f8b500 0%, #fceabb 100%)"
    },
    {
      icon: "🔄",
      title: "API Development & Integration",
      shortTitle: "API Services",
      description: "RESTful and GraphQL API development with seamless third-party integrations for enhanced functionality.",
      features: ["REST & GraphQL APIs", "Third-party Integration", "API Documentation", "Webhook Integration"],
      category: "development",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    {
      icon: "🏢",
      title: "ERP Solutions",
      shortTitle: "ERP",
      description: "Enterprise resource planning systems to streamline business processes, from accounting to inventory management.",
      features: ["Financial Management", "Supply Chain", "HR Management", "CRM Integration"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    {
      icon: "📲",
      title: "QR Code & Barcode Solutions",
      shortTitle: "QR/Barcode",
      description: "Custom QR code and barcode solutions for inventory tracking, contactless payments, and digital marketing.",
      features: ["QR Code Generation", "Barcode Scanning", "Inventory Tracking", "Analytics Dashboard"],
      category: "enterprise",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    },
    {
      icon: "🌍",
      title: "Location-Based Services",
      shortTitle: "Geolocation",
      description: "GPS-enabled applications with geofencing, location tracking, and proximity-based services.",
      features: ["Geofencing", "Real-time Tracking", "Location Analytics", "Map Integration"],
      category: "development",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
    }
  ];

  const categories = [
    { id: 'all', label: 'All Services' },
    { id: 'development', label: 'Development' },
    { id: 'enterprise', label: 'Enterprise' },
    { id: 'security', label: 'Security' },
    { id: 'design', label: 'Design' },
    { id: 'ai', label: 'AI & ML' }
  ];

  const filteredServices = activeTab === 'all'
    ? services
    : services.filter(service => service.category === activeTab);

  // Show only first 4 cards if showAll is false
  const displayedServices = showAll ? filteredServices : filteredServices.slice(0, 4);

  // JSON-LD structured data (schema.org) — lets search engines understand
  // this section as a list of Services offered by the organization.
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Our Software Development Services",
    "itemListElement": services.map((service, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Service",
        "name": service.title,
        "description": service.description,
        "serviceType": service.shortTitle,
        "category": service.category,
        "provider": {
          "@type": "Organization",
          "name": "Tracklio Hub",
          "url": "https://trackliohub.com"
        }
      }
    }))
  };

  const styles = {
    section: {
      padding: isMobile ? '60px 0' : '100px 0',
      background: 'linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)',
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
    },
    decorativeBlob: {
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(80px)',
      opacity: 0.1,
      pointerEvents: 'none',
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      paddingLeft: isMobile ? '20px' : '40px',
      paddingRight: isMobile ? '20px' : '40px',
      boxSizing: 'border-box',
      position: 'relative',
      zIndex: 1,
      width: '100%',
    },
    header: {
      textAlign: 'center',
      marginBottom: isMobile ? '40px' : '60px',
    },
    badge: {
      display: 'inline-block',
      backgroundColor: '#ede9fe',
      color: '#7c3aed',
      padding: isMobile ? '6px 16px' : '8px 20px',
      borderRadius: '50px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      marginBottom: isMobile ? '16px' : '20px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    title: {
      fontSize: isMobile ? 'clamp(28px, 8vw, 40px)' : 'clamp(40px, 5vw, 56px)',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: isMobile ? '16px' : '20px',
      lineHeight: '1.2',
      padding: '0 10px',
    },
    subtitle: {
      fontSize: isMobile ? '14px' : '18px',
      color: '#6b7280',
      maxWidth: '700px',
      margin: '0 auto',
      lineHeight: '1.8',
      padding: '0 10px',
    },
    tabsContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: isMobile ? '8px' : '12px',
      marginBottom: isMobile ? '40px' : '60px',
      flexWrap: 'wrap',
      padding: '0 10px',
    },
    tab: {
      padding: isMobile ? '8px 16px' : '12px 28px',
      borderRadius: '50px',
      border: '2px solid #e5e7eb',
      backgroundColor: '#ffffff',
      color: '#6b7280',
      fontSize: isMobile ? '12px' : '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
    },
    tabActive: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#ffffff',
      borderColor: 'transparent',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: isMobile ? '24px' : '40px',
      width: '100%',
      alignItems: 'stretch',
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: isMobile ? '16px' : '24px',
      padding: isMobile ? '24px 20px' : '40px 32px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      minHeight: isMobile ? 'auto' : '450px',
      boxSizing: 'border-box',
    },
    cardHovered: {
      transform: isMobile ? 'translateY(-4px)' : 'translateY(-12px)',
      boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
      borderColor: 'transparent',
    },
    gradientBar: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: isMobile ? '3px' : '5px',
      opacity: 0,
      transition: 'opacity 0.4s ease',
    },
    iconWrapper: {
      fontSize: isMobile ? '48px' : '56px',
      marginBottom: isMobile ? '16px' : '24px',
      display: 'inline-block',
      transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
    },
    cardTitle: {
      fontSize: isMobile ? '20px' : '24px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: isMobile ? '12px' : '16px',
      lineHeight: '1.3',
    },
    cardDescription: {
      fontSize: isMobile ? '14px' : '15px',
      color: '#6b7280',
      lineHeight: '1.7',
      marginBottom: isMobile ? '16px' : '24px',
      flex: 1,
    },
    featuresList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    featureItem: {
      fontSize: isMobile ? '13px' : '14px',
      color: '#4b5563',
      padding: isMobile ? '8px 0' : '10px 0',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px',
      borderBottom: '1px solid #f3f4f6',
    },
    checkmark: {
      width: isMobile ? '18px' : '20px',
      height: isMobile ? '18px' : '20px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '10px' : '12px',
      fontWeight: 'bold',
      flexShrink: 0,
    },
    learnMore: {
      marginTop: isMobile ? '16px' : '24px',
      color: '#667eea',
      fontSize: isMobile ? '14px' : '15px',
      fontWeight: '600',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      opacity: isMobile ? 1 : 0,
      transition: 'opacity 0.3s ease',
    },
    arrow: {
      transition: 'transform 0.3s ease',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: isMobile ? '32px' : '50px',
    },
    showMoreButton: {
      backgroundColor: '#667eea',
      color: '#ffffff',
      padding: isMobile ? '14px 32px' : '16px 48px',
      borderRadius: '50px',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    },
  };

  return (
    <section
      style={styles.section}
      id="services"
      aria-label="Our Services"
      itemScope
      itemType="https://schema.org/Service"
    >
      {/* Structured data for search engines (Service ItemList) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      {/* Decorative Background Blobs */}
      {!isMobile && (
        <>
          <div style={{...styles.decorativeBlob, top: '-100px', left: '-100px', width: '400px', height: '400px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}} />
          <div style={{...styles.decorativeBlob, bottom: '-100px', right: '-100px', width: '500px', height: '500px', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'}} />
        </>
      )}

      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.badge}>Our Services</div>
          <h2 style={styles.title} itemProp="name">
            Comprehensive Software Solutions &amp; IT Services
          </h2>
          <p style={styles.subtitle} itemProp="description">
            We provide end-to-end software development services tailored to your business needs.
            From mobile app development and web development to enterprise software, cloud &amp; DevOps,
            cybersecurity, and AI/ML solutions — our expert team delivers innovative, scalable
            technology that drives growth and digital transformation.
          </p>
        </header>

        <nav style={styles.tabsContainer} aria-label="Filter services by category">
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              style={{
                ...styles.tab,
                ...(activeTab === category.id ? styles.tabActive : {})
              }}
              onClick={() => {
                setActiveTab(category.id);
                setShowAll(false);
              }}
              onMouseEnter={(e) => {
                if (activeTab !== category.id && !isMobile) {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.color = '#667eea';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== category.id && !isMobile) {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.color = '#6b7280';
                }
              }}
              aria-pressed={activeTab === category.id}
              aria-label={`Show ${category.label} services`}
              title={`Show ${category.label} services`}
            >
              {category.label}
            </button>
          ))}
        </nav>

        <div style={styles.grid} role="list">
          {displayedServices.map((service, index) => (
            <article
              key={service.title}
              style={{
                ...styles.card,
                ...(hoveredCard === index ? styles.cardHovered : {})
              }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              aria-labelledby={`service-${index}-title`}
              aria-describedby={`service-${index}-desc`}
              itemScope
              itemType="https://schema.org/Service"
              itemProp="hasOfferCatalog"
              role="listitem"
            >
              <meta itemProp="serviceType" content={service.shortTitle} />
              <meta itemProp="category" content={service.category} />

              <div
                style={{
                  ...styles.gradientBar,
                  background: service.gradient,
                  opacity: hoveredCard === index ? 1 : 0
                }}
              />

              <div
                style={{
                  ...styles.iconWrapper,
                  transform: hoveredCard === index && !isMobile ? 'scale(1.15) rotate(5deg)' : 'scale(1) rotate(0deg)'
                }}
                role="img"
                aria-label={`${service.title} icon`}
                title={service.title}
              >
                {service.icon}
              </div>

              <h3 id={`service-${index}-title`} style={styles.cardTitle} itemProp="name">
                {service.title}
              </h3>
              <p id={`service-${index}-desc`} style={styles.cardDescription} itemProp="description">
                {service.description}
              </p>

              <ul style={styles.featuresList} aria-label={`${service.title} key features`}>
                {service.features.map((feature) => (
                  <li key={feature} style={styles.featureItem}>
                    <span
                      style={{
                        ...styles.checkmark,
                        background: hoveredCard === index ? service.gradient : '#f3f4f6',
                        color: hoveredCard === index ? '#ffffff' : '#10b981',
                      }}
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div
                style={{
                  ...styles.learnMore,
                  opacity: (hoveredCard === index || isMobile) ? 1 : 0
                }}
              >
                <span aria-label={`Learn more about ${service.title}`}>Learn More</span>
                <span
                  style={{
                    ...styles.arrow,
                    transform: hoveredCard === index ? 'translateX(4px)' : 'translateX(0)'
                  }}
                  aria-hidden="true"
                >
                  →
                </span>
              </div>
            </article>
          ))}
        </div>

        {/* Show More/Less Button */}
        {filteredServices.length > 4 && (
          <div style={styles.buttonContainer}>
            <button
              type="button"
              style={styles.showMoreButton}
              onClick={() => setShowAll(!showAll)}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = '#764ba2';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = '#667eea';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                }
              }}
              aria-expanded={showAll}
              aria-controls="services"
            >
              {showAll ? 'Show Less' : `Show More (${filteredServices.length - 4} more)`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;